import { useRef, type ReactElement, type ReactNode } from 'react';
import { useDismiss } from '../lib/use-dismiss';
import { useFocusTrap } from '../lib/use-focus-trap';

/**
 * Окно поверх панели — для действия со своей формой (сброс пароля). Подтверждение без
 * полей — `ConfirmDialog`; это окно отличается тем, что внутри произвольная форма.
 *
 * `Esc` закрывает окно, а не панель под ним: панель проверяет `aria-modal` перед тем,
 * как закрыться сама.
 */
export function Modal({
  title,
  onCancel,
  children,
}: {
  readonly title: string;
  readonly onCancel: () => void;
  readonly children: ReactNode;
}): ReactElement {
  const box = useRef<HTMLDivElement>(null);

  useDismiss(true, onCancel);
  useFocusTrap(box, true);

  return (
    <div
      ref={box}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 30,
        padding: 'var(--sp-4)',
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-lg)',
          padding: 'var(--sp-6)',
          width: 'min(480px, 100%)',
          maxHeight: '100%',
          overflow: 'auto',
          display: 'grid',
          gap: 'var(--sp-3)',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}
